import React, { useEffect, useRef, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { FiBarChart2, FiClock, FiLayers, FiInstagram, FiYoutube } from 'react-icons/fi';
import { API_BASE_URL } from '../config/api';
import '../styles/HomePage.css';

const HomePage: React.FC = () => {
  const { isSignedIn } = useAuth();
  const { redirectToSignIn } = useClerk();
  const [tracks, setTracks] = useState<string[] | null>(null);
  const [tracksFailed, setTracksFailed] = useState(false);
  const marqueeRef = useRef<HTMLDivElement>(null);

  const handleLogin = () => {
    redirectToSignIn({ redirectUrl: '/dashboard' });
  };

  // Mouse drag-to-scrub on the tracks marquee. Touch devices use the
  // native overflow-x scroll on the container; the handler bails on
  // non-mouse pointers so vertical page scroll is never blocked.
  // Once the user starts dragging, the CSS keyframe animation is
  // disabled for the rest of the session — otherwise transform-based
  // animation and scrollLeft would compose into a visual jump when
  // the cursor leaves and the animation resumes.
  useEffect(() => {
    const el = marqueeRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const inner = el.querySelector('.tracks-marquee-track') as HTMLElement | null;
      if (inner) inner.style.animation = 'none';
      isDown = true;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      el.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      e.preventDefault();
      el.scrollLeft = startScrollLeft - (e.clientX - startX);
    };
    const endDrag = (e: PointerEvent) => {
      if (!isDown) return;
      isDown = false;
      try { el.releasePointerCapture(e.pointerId); } catch {}
    };

    // Wrap scrollLeft when the user reaches either edge of the doubled list:
    // jump silently by half the inner width to the equivalent position in
    // the other copy, so the marquee feels infinite under manual scroll too.
    const onScroll = () => {
      const half = el.scrollWidth / 2;
      if (el.scrollLeft <= 0) {
        el.scrollLeft = half;
      } else if (el.scrollLeft >= el.scrollWidth - el.clientWidth) {
        el.scrollLeft = el.scrollLeft - half;
      }
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('scroll', onScroll);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('scroll', onScroll);
    };
  }, [tracks]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.target.classList.toggle('visible', e.isIntersecting)),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.animate-in').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/public/tracks`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then((data: { name: string }[]) => setTracks(data.map(t => t.name)))
      .catch(() => setTracksFailed(true));
  }, []);

  return (
    <div className="home-container">
      {/* Floating socials, top-right */}
      <div className="floating-socials">
        <a
          href="https://instagram.com/gpcommit"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="GPCommit on Instagram"
          className="floating-social-icon"
        >
          <FiInstagram size={20} />
        </a>
        <a
          href="https://youtube.com/@gpcommit"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="GPCommit on YouTube"
          className="floating-social-icon"
        >
          <FiYoutube size={20} />
        </a>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-logo hero-fade-in" style={{ animationDelay: '0s' }}>
            <div className="logo-wrapper">
              <img src="/gp_commit_logo_1.png" alt="GPCommit Logo" className="logo" />
            </div>
          </div>
          <h1>
            <span className="hero-word" style={{ animationDelay: '0.15s' }}>Drive</span>{' '}
            <span className="hero-word" style={{ animationDelay: '0.24s' }}>Faster</span>{' '}
            <span className="hero-word" style={{ animationDelay: '0.33s' }}>on</span>{' '}
            <span className="hero-word" style={{ animationDelay: '0.42s' }}>Track</span>
          </h1>
          <p className="hero-subtitle hero-fade-in" style={{ animationDelay: '0.6s' }}>
            Data logging, analytics, and video overlays for improving your lap times on the racetrack.
          </p>
          <div className="hero-buttons hero-fade-in" style={{ animationDelay: '0.75s' }}>
            {!isSignedIn ? (
              <button onClick={handleLogin} className="primary-button">
                Get Started
              </button>
            ) : (
              <a href="/dashboard" className="primary-button">Go to App</a>
            )}
            <a href="https://apps.apple.com/us/app/gpcommit/id6758561247" target="_blank" rel="noopener noreferrer">
              <img src="/app-store-badge.svg" alt="Download on the App Store" />
            </a>
          </div>
        </div>
        <div className="hero-image">
          <img src="/gp_commit_ui.png" alt="GPCommit racing analytics dashboard showing lap time analysis, track visualization, and performance insights for motorsport data optimization" />
        </div>
        <div className="hero-video">
          <video
            src="/gp_commit_demo.mp4"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label="GPCommit mobile app demo"
          />
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2>Why Choose GPCommit?</h2>
        <div className="features-grid">
          <div className="feature-card animate-in">
            <div className="feature-icon">
              <FiLayers size={26} />
            </div>
            <h3>Multi-Platform</h3>
            <p>
              Record your lap times using our iOS or Android apps to quickly understand your performance. Data is immediately available on a web dashboard for deeper analysis, and it can be viewed on any device. 
            </p>
          </div>

          <div className="feature-card animate-in">
            <div className="feature-icon">
              <FiClock size={26} />
            </div>
            <h3>Analysis You Can Understand</h3>
            <p>
              Learn the parts of the lap where you are loosing time, down to specific braking zones and corners. Tackle your laps one corner at a time until you reach your maximum potential. Feedback is simple and actionable without fancy plots and information overload.
            </p>
          </div>

          <div className="feature-card animate-in">
            <div className="feature-icon">
              <FiBarChart2 size={26} />
            </div>
            <h3>Unique Lap Optimization </h3>
            <p>
              Most coaching applications compare your driving to your best times in a given section. We compare your driving to your car's maximum capabilities during a driving session.
            </p>
          </div>
          
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section animate-in">
        <h2>Seriously Free</h2>
        <p className="pricing-subtitle">
          Most apps charge you before you hit the track. We don't.
        </p>
        <div className="free-features">
          {[
            'GPS Data Logging',
            'Dual Camera Recording',
            'Lap Times & Delta Analysis',
            'Video Overlays',
            'Web Dashboard',
            'Unlimited Sessions',
          ].map(item => (
            <span key={item} className="free-feature-item">{item}</span>
          ))}
        </div>
        <div className="pricing-paid">
          <span className="pricing-paid-label">The only thing that costs money</span>
          <div className="pricing-paid-detail">
            <strong>Optimal Lap Analysis</strong> — compares your driving to your car's physical limits, not just your personal bests. <span className="pricing-credit">$1 per analysis.</span>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <h2>How It Works</h2>
        <div className="steps-list">
          <div className="step-item animate-in">
            <h3>1 - Download the GPCommit app</h3>
            <p>Log telemetry and record from one or both cameras right on your phone.</p>
          </div>

          <div className="step-item animate-in">
            <h3>2 - Review on-track</h3>
            <p>Browse your laps, check lap times, and explore session results from your phone between sessions. Create and post video overlays.</p>
          </div>

          <div className="step-item animate-in">
            <h3>3 - Go deeper on the web</h3>
            <p>Open the web dashboard to run optimal lap analysis, which compares your driving to your car's physical limits — not just your personal bests.</p>
          </div>

          <div className="step-item animate-in">
            <h3>Already have a logger?</h3>
            <p>Upload your session data directly to the web dashboard. We support exports from TrackAddict, AIM Solo, VBOX, and others.</p>
          </div>
        </div>
      </section>

      {/* Supported Devices Section */}
      <section className="devices-section">
        <h2>Supported External GPS Loggers</h2>
        <p className="devices-subtitle">
          Use your phone's built-in GPS or pair a dedicated Bluetooth GPS receiver for higher accuracy and update rates.
        </p>
        <div className="devices-grid">
          <div className="device-card animate-in">
            <div className="device-image-wrap">
              <img src="/xgps160.jpg" alt="Dual XGPS160 GPS receiver" className="device-img" />
            </div>
            <div className="device-info">
              <h3>Dual XGPS160</h3>
              <div className="device-frequency">10 Hz</div>
              <p>Bluetooth GPS + GLONASS receiver with 10 Hz update rate. Dual satellite systems and 10x more position updates per second for precise corner analysis.</p>
            </div>
            <a
              href="https://amzn.to/4up1upf"
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="device-buy-button"
            >
              Buy on Amazon
            </a>
          </div>

          <div className="device-card animate-in">
            <div className="device-image-wrap">
              <img src="/garmin_glo2.png" alt="Garmin GLO 2 GPS receiver" className="device-img" />
            </div>
            <div className="device-info">
              <h3>Garmin GLO 2</h3>
              <div className="device-frequency">1 Hz</div>
              <p>Bluetooth GPS + GLONASS receiver. Compact and reliable for lap timing on iOS and Android.</p>
            </div>
            <a
              href="https://amzn.to/4900akg"
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="device-buy-button"
            >
              Buy on Amazon
            </a>
          </div>
        </div>
      </section>

      {/* Supported Tracks Section */}
      <section className="tracks-section">
        <h2>Supported Tracks</h2>
        <p className="tracks-subtitle">
          Don't see your track? Email us at{' '}
          <a href="mailto:info@gpcommit.com" className="tracks-email">info@gpcommit.com</a>
          {' '}or connect on{' '}
          <a
            href="https://instagram.com/gpcommit"
            target="_blank"
            rel="noopener noreferrer"
            className="tracks-email"
          >socials</a>.
        </p>
        {tracksFailed ? (
          <p className="tracks-unavailable">Track list temporarily unavailable.</p>
        ) : tracks ? (
          <div className="tracks-marquee" aria-label="Supported tracks" ref={marqueeRef}>
            <div className="tracks-marquee-track">
              {[...tracks, ...tracks].map((name, i) => (
                <span key={i} className="track-pill" aria-hidden={i >= tracks.length}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        ) : null}

      </section>
    </div>
  );
};

export default HomePage;
